import React, { useId } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@dashboard/components/ui/collapsible';
import { ItemContent, ItemDescription, ItemLink, ItemTitle } from '@dashboard/components/ui/item';
import GuideBadge from './GuideBadge';

const guideCount = count => `${count} ${count === 1 ? 'guide' : 'guides'}`;

export default function GuideList({ groups, openGroups, onOpenGroupsChange, onSelect, onClose }) {
  const headingId = useId();
  if (groups.length === 0) return null;

  const setGroupOpen = (tab, open) =>
    onOpenGroupsChange(open ? [...openGroups, tab] : openGroups.filter(other => other !== tab));

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 id={headingId} className="text-xs font-semibold leading-4 text-muted-foreground">
          Guides
        </h3>
        <div className="flex items-center gap-1">
          <span className="text-xs leading-4 text-muted-foreground">
            or type{' '}
            <GuideBadge asChild className="px-1.5">
              <kbd>/</kbd>
            </GuideBadge>{' '}
            in the message box
          </span>
          {onClose && (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Close guides"
              className="text-muted-foreground"
              onClick={onClose}
            >
              <X aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
      {groups.map(group => (
        <Collapsible
          key={group.tab}
          open={openGroups.includes(group.tab)}
          onOpenChange={open => setGroupOpen(group.tab, open)}
          className="flex flex-col gap-1.5"
        >
          <h4>
            <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 rounded-md px-0.5 py-1 text-left text-[13px] font-medium leading-[18px] outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
              <span>
                {group.tabLabel}
                <span className="font-normal text-muted-foreground">
                  {' · '}
                  {guideCount(group.guides.length)}
                </span>
              </span>
              <ChevronDown
                aria-hidden="true"
                className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
              />
            </CollapsibleTrigger>
          </h4>
          <CollapsibleContent asChild>
            <ul className="overflow-hidden rounded-[10px] border">
              {group.guides.map(guide => (
                <li key={guide.key} className="border-t first:border-t-0">
                  <ItemLink
                    asChild
                    className="min-h-12 w-full rounded-none py-1.5 text-left hover:bg-muted"
                  >
                    <button type="button" onClick={() => onSelect(guide.key)}>
                      <ItemContent>
                        <ItemTitle className="text-[13px] leading-[18px]">
                          {guide.section.label}
                        </ItemTitle>
                        <ItemDescription className="leading-4 text-muted-foreground">
                          {guide.summary}
                        </ItemDescription>
                      </ItemContent>
                      <GuideBadge>{guide.command}</GuideBadge>
                    </button>
                  </ItemLink>
                </li>
              ))}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      ))}
    </section>
  );
}
